import * as Sentry from '@sentry/react';
import { useState, useEffect } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '@/components/ui/dialog';
import {
    Collapsible,
    CollapsibleTrigger,
    CollapsibleContent,
} from '@/components/ui/collapsible';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
} from '@/components/ui/sheet';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import {
    Command,
    CommandInput,
    CommandList,
    CommandEmpty,
    CommandGroup,
    CommandItem,
} from '@/components/ui/command';
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table';
import { Label } from '@/components/ui/label';
import {
    ShoppingCart,
    Plus,
    Trash2,
    Search,
    History,
    CheckCircle2,
    Loader2,
    DollarSign,
    User as UserIcon,
    AlertCircle,
    ChevronDown,
    X
} from 'lucide-react';
import { gymApi } from '@/lib/api';
import { InventoryItem, Client, Sale, SaleItem, CashBox, CashMovement } from '@/types';
import { useAuth } from '@/contexts/AuthContext';
import { useIsMobile } from '@/hooks/use-mobile';
import { useToast } from '@/hooks/use-toast';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

export default function SalesPage() {
    const { gym, user } = useAuth();
    const [inventory, setInventory] = useState<InventoryItem[]>([]);
    const [clients, setClients] = useState<Client[]>([]);
    const [sales, setSales] = useState<Sale[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [activeTab, setActiveTab] = useState<'pos' | 'history'>('pos');

    // Petty Cash State
    const [activeBox, setActiveBox] = useState<(CashBox & { cash_movements: CashMovement[] }) | null>(null);
    const [isOpeningBox, setIsOpeningBox] = useState(false);
    const [openingBalance, setOpeningBalance] = useState(0);
    const [isClosingBox, setIsClosingBox] = useState(false);
    const [actualBalance, setActualBalance] = useState(0);
    const [closingNotes, setClosingNotes] = useState('');
    const [isMovementOpen, setIsMovementOpen] = useState(false);
    const [movementType, setMovementType] = useState<'in' | 'out'>('in');
    const [movementAmount, setMovementAmount] = useState(0);
    const [movementReason, setMovementReason] = useState('');

    // New Sale State
    const [cart, setCart] = useState<{ item: InventoryItem; quantity: number }[]>([]);
    const [selectedClientId, setSelectedClientId] = useState<string>('');
    const [selectedClientName, setSelectedClientName] = useState<string>('');
    const [clientSearch, setClientSearch] = useState('');
    const [clientResults, setClientResults] = useState<Client[]>([]);
    const [isSearchingClients, setIsSearchingClients] = useState(false);
    const [clientPopoverOpen, setClientPopoverOpen] = useState(false);
    const [paymentMethod, setPaymentMethod] = useState<string>('cash');
    const [productSearch, setProductSearch] = useState('');
    const [receivedAmount, setReceivedAmount] = useState<number | string>('');
    const [boxHistory, setBoxHistory] = useState<(CashBox & { cash_movements: CashMovement[] })[]>([]);
    const [selectedHistoryBox, setSelectedHistoryBox] = useState<(CashBox & { cash_movements: CashMovement[] }) | null>(null);
    const [isHistoryLoading, setIsHistoryLoading] = useState(false);

    const { toast } = useToast();
    const isMobile = useIsMobile();

    useEffect(() => {
        if (!gym?.gym_id) return;
        if (clientSearch.trim().length < 2) {
            setClientResults([]);
            return;
        }
        const timer = setTimeout(async () => {
            setIsSearchingClients(true);
            try {
                const res = await gymApi.findClientByIdentifier(gym.gym_id, clientSearch.trim());
                if (res.success) setClientResults(res.clients || []);
            } catch { setClientResults([]); }
            finally { setIsSearchingClients(false); }
        }, 300);
        return () => clearTimeout(timer);
    }, [clientSearch, gym?.gym_id]);

    useEffect(() => {
        if (gym?.gym_id) {
            fetchData();
            loadHistory();
        }
    }, [gym?.gym_id]);

    async function fetchData() {
        setIsLoading(true);
        let hasErrors = false;

        try {
            const invRes = await gymApi.listInventory(null, gym!.gym_id);
            if (invRes.success) setInventory(invRes.items.filter((i: any) => i.quantity > 0));
        } catch (e) {
            console.error('Failed to fetch inventory:', e);
            Sentry.captureException(e, { tags: { operation: 'fetch_inventory' } });
            hasErrors = true;
        }

        try {
            const cliRes = await gymApi.listClients(null, gym!.gym_id);
            if (cliRes.success) setClients(cliRes.clients);
        } catch (e) {
            console.error('Failed to fetch clients:', e);
            Sentry.captureException(e, { tags: { operation: 'fetch_clients' } });
            hasErrors = true;
        }

        try {
            const salesRes = await gymApi.listSales(gym!.gym_id);
            if (salesRes.success) setSales(salesRes.sales);
        } catch (e) {
            console.error('Failed to fetch sales:', e);
            Sentry.captureException(e, { tags: { operation: 'fetch_sales' } });
            hasErrors = true;
        }

        try {
            const boxRes = await gymApi.getActiveBox(gym!.gym_id);
            if (boxRes.success) setActiveBox(boxRes.box);
        } catch (e) {
            console.error('Failed to fetch active box:', e);
            Sentry.captureException(e, { tags: { operation: 'fetch_active_box' } });
            hasErrors = true;
        }

        if (hasErrors) {
            toast({ title: 'Algunos datos no se pudieron cargar', description: 'Intenta recargar la página si faltan datos.', variant: 'destructive' });
        }

        setIsLoading(false);
    };

    async function loadHistory() {
        if (!gym?.gym_id) return;
        setIsHistoryLoading(true);
        try {
            const res = await gymApi.getBoxHistory(gym.gym_id);
            if (res.success) {
                console.log('[loadHistory] Boxes loaded:', res.history?.length || 0);
                setBoxHistory(res.history || []);
            }
        } catch (error) {
            console.error('Failed to load history:', error);
            Sentry.captureException(error, {
                tags: { operation: 'load_box_history' },
                extra: { gym_id: gym?.gym_id }
            });
            toast({ title: 'Error', description: 'No se pudo cargar el historial.', variant: 'destructive' });
        } finally {
            setIsHistoryLoading(false);
        }
    };

    const addToCart = (product: InventoryItem) => {
        const existing = cart.find(c => c.item.item_id === product.item_id);
        if (existing) {
            if (existing.quantity >= product.quantity) {
                toast({
                    title: 'Stock insuficiente',
                    description: `Solo hay ${product.quantity} unidades disponibles.`,
                    variant: 'destructive',
                });
                return;
            }
            setCart(cart.map(c =>
                c.item.item_id === product.item_id
                    ? { ...c, quantity: c.quantity + 1 }
                    : c
            ));
        } else {
            setCart([...cart, { item: product, quantity: 1 }]);
        }
    };

    const removeFromCart = (productId: string) => {
        setCart(cart.filter(c => c.item.item_id !== productId));
    };

    const updateCartQuantity = (productId: string, qty: number) => {
        const item = inventory.find(i => i.item_id === productId);
        if (!item) return;

        if (qty > item.quantity) {
            toast({
                title: 'Stock insuficiente',
                description: `Solo hay ${item.quantity} unidades disponibles.`,
                variant: 'destructive',
            });
            qty = item.quantity;
        }

        if (qty <= 0) {
            removeFromCart(productId);
        } else {
            setCart(cart.map(c => c.item.item_id === productId ? { ...c, quantity: qty } : c));
        }
    };

    const totalAmount = cart.reduce((sum, c) => sum + (c.item.selling_price * c.quantity), 0);

    const getCashSuggestions = (total: number) => {
        const bills = [1, 5, 10, 20, 50, 100];
        const suggestions = bills.filter(bill => bill > total);

        // Always try to have 3 suggestions. 
        // If we don't have enough higher bills, we can suggest common combinations or rounded amounts
        while (suggestions.length < 3) {
            const lastMax = suggestions.length > 0 ? suggestions[suggestions.length - 1] : total;
            const nextStep = lastMax <= 20 ? lastMax + 5 : lastMax + 10;
            if (!suggestions.includes(nextStep)) suggestions.push(nextStep);
            if (suggestions.length > 5) break; // Safety break
        }

        return suggestions.slice(0, 3);
    };

    const handleCompleteSale = async () => {
        if (cart.length === 0) return;
        setIsSubmitting(true);
        try {
            const saleData = {
                gym_id: gym!.gym_id,
                client_id: (selectedClientId && selectedClientId !== 'none') ? selectedClientId : undefined,
                total_amount: totalAmount,
                payment_method: paymentMethod,
                items: cart.map(c => ({
                    product_id: c.item.item_id,
                    quantity: c.quantity,
                    unit_price: c.item.selling_price
                }))
            };

            const res = await gymApi.createSale(saleData);
            if (res.success) {
                toast({
                    title: 'Venta completada',
                    description: 'La venta ha sido registrada exitosamente y el stock actualizado.',
                });
                setCart([]);
                setSelectedClientId('');
                setSelectedClientName('');
                setReceivedAmount('');
                await fetchData();
                setActiveTab('history');
            }
        } catch (error: any) {
            console.error("Sale Error:", error);
            Sentry.captureException(error, {
                tags: { operation: 'pos_sale' },
                extra: { client_id: selectedClientId, total: totalAmount, itemsCount: cart.length }
            });
            toast({
                title: 'Error al registrar venta',
                description: error?.message || 'No se pudo registrar la venta.',
                variant: 'destructive',
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleOpenBox = async () => {
        if (!gym?.gym_id || !user?.user_id) return;
        try {
            const res = await gymApi.openBox(gym.gym_id, user.user_id, openingBalance);
            if (res.success) {
                toast({ title: 'Caja abierta', description: `Turno iniciado con $${openingBalance}` });
                setIsOpeningBox(false);
                await fetchData();
            }
        } catch (error: any) {
            console.error("Error opening cash box:", error);
            Sentry.captureException(error, {
                tags: { operation: 'open_cash_box' },
                extra: { initialBalance: openingBalance, gym_id: gym?.gym_id }
            });
            toast({ title: 'Error al abrir caja', description: error?.message || 'No se pudo abrir la caja.', variant: 'destructive' });
        }
    };

    const handleCloseBox = async () => {
        if (!activeBox || !user?.user_id) return;
        try {
            const res = await gymApi.closeBox(activeBox.id, user.user_id, actualBalance, closingNotes);
            if (res.success) {
                toast({ title: 'Caja cerrada', description: 'El arqueo ha sido registrado.' });
                setIsClosingBox(false);
                setActiveBox(null);
                fetchData();
                loadHistory();
            }
        } catch (error: any) {
            console.error("Error closing cash box:", error);
            Sentry.captureException(error, {
                tags: { operation: 'close_cash_box' },
                extra: { boxId: activeBox.id, gym_id: gym?.gym_id }
            });
            toast({ title: 'Error al cerrar caja', description: error?.message || 'No se pudo cerrar la caja.', variant: 'destructive' });
        }
    };

    const handleAddMovement = async () => {
        if (!activeBox) return;
        try {
            const res = await gymApi.addCashMovement(activeBox.id, movementAmount, movementType, movementReason);
            if (res.success) {
                toast({ title: 'Movimiento registrado', description: `Se registró un ${movementType === 'in' ? 'ingreso' : 'egreso'} de $${movementAmount}` });
                setIsMovementOpen(false);
                setMovementAmount(0);
                setMovementReason('');
                await fetchData();
            }
        } catch (error: any) {
            toast({ title: 'Error al registrar movimiento', description: error?.message || 'No se pudo registrar el movimiento.', variant: 'destructive' });
        }
    };

    const renderSessionDetailBody = (box: CashBox & { cash_movements: CashMovement[] }) => (
        <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card className="bg-muted/30">
                    <CardContent className="pt-6">
                        <div className="text-xs font-bold text-muted-foreground uppercase mb-1">Fondo Inicial</div>
                        <div className="text-2xl font-black">${box.opening_balance.toFixed(2)}</div>
                    </CardContent>
                </Card>
                <Card className="bg-primary/5 border-primary/10">
                    <CardContent className="pt-6">
                        <div className="text-xs font-bold text-primary uppercase mb-1">Total Movimientos</div>
                        <div className="text-2xl font-black">
                            ${box.cash_movements.reduce((sum, m) => sum + (m.type === 'in' ? Number(m.amount) : -Number(m.amount)), 0).toFixed(2)}
                        </div>
                    </CardContent>
                </Card>
                <Card className="bg-slate-900 text-white">
                    <CardContent className="pt-6">
                        <div className="text-xs font-bold text-slate-400 uppercase mb-1">Cierre Final (Arqueo)</div>
                        <div className="text-2xl font-black">${box.actual_balance?.toFixed(2) || '0.00'}</div>
                    </CardContent>
                </Card>
            </div>

            <Card>
                <CardHeader className="pb-2">
                    <CardTitle className="text-sm font-bold flex items-center gap-2 uppercase">
                        <History className="h-4 w-4" />
                        Actividad Detallada
                    </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                    <div className="max-h-[500px] overflow-y-auto divide-y">
                        {[
                            ...box.cash_movements
                                .filter(m => !m.reason.startsWith('Venta de productos'))
                                .map(m => ({
                                    time: m.created_at,
                                    reason: m.reason,
                                    type: m.type,
                                    amount: m.amount,
                                    isSale: false,
                                    isOpening: false
                                })),
                            ...sales.filter(s => (s as any).cash_box_id === box.id).map(s => ({
                                time: s.created_at,
                                reason: s.client_name ? `Venta: ${s.client_name}` : 'Venta: Consumidor Final',
                                type: 'in' as const,
                                amount: s.total_amount,
                                isSale: true,
                                isOpening: false,
                                items: s.items
                            })),
                            {
                                time: box.opened_at,
                                reason: 'Apertura de Caja (Capital Inicial)',
                                type: 'in' as const,
                                amount: box.opening_balance,
                                isSale: false,
                                isOpening: true
                            }
                        ].sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime()).map((m, idx) => (
                            (m.isSale && (m as any).items && (m as any).items.length > 0) ? (
                                <Collapsible key={idx}>
                                    <CollapsibleTrigger className="w-full p-4 flex justify-between items-center hover:bg-muted/50 transition-colors group data-[state=open]:bg-muted/30">
                                        <div className="flex flex-col min-w-0 mr-2">
                                            <span className="text-[10px] text-muted-foreground text-left">{format(new Date(m.time), "HH:mm")}</span>
                                            <span className="text-sm font-medium">{m.reason}</span>
                                        </div>
                                        <div className="flex items-center gap-1.5">
                                            <div className="flex flex-col items-end">
                                                <span className={`text-sm font-bold ${m.type === 'in' ? 'text-green-500' : 'text-destructive'}`}>
                                                    {m.type === 'in' ? '+' : '-'}${m.amount.toFixed(2)}
                                                </span>
                                                <Badge className="text-[8px] h-3 px-1" variant="secondary">VENTA</Badge>
                                            </div>
                                            <ChevronDown className="h-4 w-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180 shrink-0" />
                                        </div>
                                    </CollapsibleTrigger>
                                    <CollapsibleContent>
                                        <div className="px-5 pb-3 space-y-0.5">
                                            {(m as any).items.map((item: SaleItem) => (
                                                <div key={item.id} className="flex justify-between items-center text-xs text-muted-foreground pl-4 border-l-2 border-primary/20 py-0.5">
                                                    <span className="truncate flex-1 mr-2">
                                                        {item.product_name}
                                                        <span className="text-muted-foreground/50 ml-1">×{item.quantity}</span>
                                                    </span>
                                                    <span className="font-mono font-medium">${(item.unit_price * item.quantity).toFixed(2)}</span>
                                                </div>
                                            ))}
                                            <div className="text-[10px] text-muted-foreground/50 pl-4 pt-1">
                                                {(m as any).items.length} producto{(m as any).items.length !== 1 ? 's' : ''}
                                            </div>
                                        </div>
                                    </CollapsibleContent>
                                </Collapsible>
                            ) : (
                                <div key={idx} className="p-4 flex justify-between items-center group hover:bg-muted/50 transition-colors">
                                    <div className="flex flex-col min-w-0 mr-2">
                                        <span className="text-[10px] text-muted-foreground">{format(new Date(m.time), "HH:mm")}</span>
                                        <span className="text-sm font-medium">{m.reason}</span>
                                    </div>
                                    <div className="flex flex-col items-end">
                                        <span className={`text-sm font-bold ${m.type === 'in' ? 'text-green-500' : 'text-destructive'}`}>
                                            {m.type === 'in' ? '+' : '-'}${m.amount.toFixed(2)}
                                        </span>
                                        {m.isOpening && <Badge className="text-[8px] h-3 px-1 bg-green-500/10 text-green-600 border-green-500/20" variant="outline">CAPITAL</Badge>}
                                    </div>
                                </div>
                            )
                        ))}
                    </div>
                </CardContent>
            </Card>

            {box.notes && (
                <Card className="border-amber-500/20 bg-amber-500/5">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-xs font-bold uppercase text-amber-600">Notas de Cierre</CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-sm italic text-amber-900/80">"{box.notes}"</p>
                    </CardContent>
                </Card>
            )}
        </div>
    );

    const filteredProducts = inventory.filter(i =>
        i.name.toLowerCase().includes(productSearch.toLowerCase()) ||
        i.category.toLowerCase().includes(productSearch.toLowerCase())
    );

    if (isLoading) {
        return (
            <DashboardLayout title="Ventas">
                <div className="flex items-center justify-center h-[60vh]">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            </DashboardLayout>
        );
    }

    const currentCashBalance = activeBox ?
        activeBox.opening_balance +
        activeBox.cash_movements.reduce((sum, m) => sum + (m.type === 'in' ? Number(m.amount) : -Number(m.amount)), 0)
        : 0;

    return (
        <DashboardLayout title="Punto de Venta" subtitle="Ventas e inventario unificados con caja chica">
            <div className="flex gap-2 sm:gap-4 mb-6">
                <Button
                    variant={activeTab === 'pos' ? 'gradient' : 'outline'}
                    onClick={() => setActiveTab('pos')}
                    className="flex-1 h-12 sm:h-10 px-2 sm:px-4"
                >
                    <Plus className="mr-1 sm:mr-2 h-4 w-4 flex-shrink-0" />
                    <span className="truncate">Terminal POS</span>
                    {activeBox && (
                        <Badge variant="secondary" className="ml-1 sm:ml-2 bg-green-500/10 text-green-500 border-green-500/20 px-1.5 sm:px-2 flex-shrink-0">
                            <span className="hidden sm:inline">Caja Abierta</span>
                            <div className="h-2 w-2 rounded-full bg-green-500 sm:hidden" />
                        </Badge>
                    )}
                </Button>
                <Button
                    variant={activeTab === 'history' ? 'gradient' : 'outline'}
                    onClick={() => setActiveTab('history')}
                    className="flex-1 h-12 sm:h-10 px-2 sm:px-4"
                >
                    <History className="mr-1 sm:mr-2 h-4 w-4 flex-shrink-0" />
                    <span className="truncate">Historial Ventas</span>
                </Button>
            </div>

            {activeTab === 'pos' ? (
                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
                    {/* Catalog & Cart (Main Area) */}
                    <div className="lg:col-span-3 space-y-6">
                        <Card>
                            <CardHeader className="px-4 py-3 sm:px-6 sm:py-6">
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <CardTitle className="text-lg">Productos Disponibles</CardTitle>
                                    <div className="relative w-full sm:w-64">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                        <Input
                                            placeholder="Buscar producto..."
                                            className="pl-9 h-10 sm:h-9"
                                            value={productSearch}
                                            onChange={(e) => setProductSearch(e.target.value)}
                                        />
                                    </div>
                                </div>
                            </CardHeader>
                            <CardContent>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    {filteredProducts.map(product => (
                                        <div
                                            key={product.item_id}
                                            className="p-4 rounded-xl border border-border hover:border-primary/50 transition-colors cursor-pointer group"
                                            onClick={() => addToCart(product)}
                                        >
                                            <div className="flex justify-between items-start mb-2">
                                                <div>
                                                    <h4 className="font-semibold group-hover:text-primary transition-colors">{product.name}</h4>
                                                    <p className="text-xs text-muted-foreground">{product.category}</p>
                                                </div>
                                                <Badge variant="outline" className="bg-primary/5 text-primary border-primary/20">
                                                    ${product.selling_price.toFixed(2)}
                                                </Badge>
                                            </div>
                                            <div className="flex justify-between items-center mt-4">
                                                <span className="text-xs text-muted-foreground">Stock: {product.quantity} {product.unit}</span>
                                                <Button size="sm" variant="ghost" className="h-8 w-8 p-0 rounded-full">
                                                    <Plus className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </div>
                                    ))}
                                    {filteredProducts.length === 0 && (
                                        <div className="col-span-2 text-center py-8 text-muted-foreground">
                                            No hay productos disponibles con stock.
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>

                        {/* Cart & Finalization */}
                        <div className="space-y-6">
                            <Card>
                                <CardHeader>
                                    <CardTitle className="text-lg flex items-center gap-2">
                                        <ShoppingCart className="h-5 w-5" />
                                        Carrito
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <div className="max-h-[300px] overflow-y-auto space-y-3 pr-2">
                                        {cart.map(c => (
                                            <div key={c.item.item_id} className="flex gap-3 justify-between items-center group">
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-sm truncate">{c.item.name}</p>
                                                    <p className="text-xs text-muted-foreground">${c.item.selling_price.toFixed(2)} c/u</p>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                    <Input
                                                        type="number"
                                                        value={c.quantity}
                                                        onChange={(e) => updateCartQuantity(c.item.item_id, parseInt(e.target.value))}
                                                        className="w-16 h-8 text-center"
                                                    />
                                                    <Button
                                                        size="icon"
                                                        variant="ghost"
                                                        className="h-8 w-8 text-destructive sm:opacity-0 sm:group-hover:opacity-100 transition-opacity"
                                                        onClick={() => removeFromCart(c.item.item_id)}
                                                    >
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </div>
                                        ))}
                                        {cart.length === 0 && (
                                            <p className="text-center text-muted-foreground py-4 text-sm">El carrito está vacío</p>
                                        )}
                                    </div>

                                    <div className="pt-4 border-t space-y-4">
                                        <div className="space-y-2">
                                            <Label className="text-xs uppercase text-muted-foreground">Cliente (Opcional)</Label>
                                            <Popover open={clientPopoverOpen} onOpenChange={setClientPopoverOpen}>
                                                <PopoverTrigger asChild>
                                                    <Button
                                                        variant="outline"
                                                        className="w-full justify-between h-10 font-normal"
                                                    >
                                                        {selectedClientName ? (
                                                            <span className="text-foreground">{selectedClientName}</span>
                                                        ) : (
                                                            <span className="text-muted-foreground">Buscar cliente...</span>
                                                        )}
                                                        {selectedClientName ? (
                                                            <button
                                                                className="ml-2 hover:bg-muted rounded-full p-0.5"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    setSelectedClientId('');
                                                                    setSelectedClientName('');
                                                                }}
                                                            >
                                                                <X className="h-3 w-3 text-muted-foreground" />
                                                            </button>
                                                        ) : (
                                                            <Search className="h-4 w-4 text-muted-foreground shrink-0" />
                                                        )}
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-[--radix-popover-trigger-width] p-0" align="start">
                                                    <Command shouldFilter={false}>
                                                        <CommandInput
                                                            placeholder="Nombre o telefono del cliente..."
                                                            value={clientSearch}
                                                            onValueChange={setClientSearch}
                                                        />
                                                        <CommandList>
                                                            <CommandEmpty>
                                                                {isSearchingClients ? (
                                                                    <div className="flex items-center justify-center py-6">
                                                                        <Loader2 className="h-4 w-4 animate-spin" />
                                                                    </div>
                                                                ) : clientSearch.length < 2 ? (
                                                                    'Escribe al menos 2 caracteres para buscar'
                                                                ) : (
                                                                    'No se encontraron clientes'
                                                                )}
                                                            </CommandEmpty>
                                                            <CommandGroup>
                                                                <CommandItem
                                                                    value="none"
                                                                    onSelect={() => {
                                                                        setSelectedClientId('');
                                                                        setSelectedClientName('Consumidor Final');
                                                                        setClientPopoverOpen(false);
                                                                        setClientSearch('');
                                                                    }}
                                                                >
                                                                    <UserIcon className="h-4 w-4 mr-2 text-muted-foreground" />
                                                                    Consumidor Final
                                                                </CommandItem>
                                                                {clientResults.map(client => (
                                                                    <CommandItem
                                                                        key={client.client_id}
                                                                        value={client.name}
                                                                        onSelect={() => {
                                                                            setSelectedClientId(client.client_id);
                                                                            setSelectedClientName(client.name);
                                                                            setClientPopoverOpen(false);
                                                                            setClientSearch('');
                                                                        }}
                                                                    >
                                                                        <div className="flex items-center gap-2 w-full">
                                                                            <UserIcon className="h-4 w-4 text-primary shrink-0" />
                                                                            <div className="flex-1 min-w-0">
                                                                                <p className="text-sm font-medium truncate">{client.name}</p>
                                                                                {client.phone && (
                                                                                    <p className="text-xs text-muted-foreground truncate">{client.phone}</p>
                                                                                )}
                                                                            </div>
                                                                        </div>
                                                                    </CommandItem>
                                                                ))}
                                                            </CommandGroup>
                                                        </CommandList>
                                                    </Command>
                                                </PopoverContent>
                                            </Popover>
                                        </div>

                                        <div className="space-y-2">
                                            <Label className="text-xs uppercase text-muted-foreground">Método de Pago</Label>
                                            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                                                <SelectTrigger>
                                                    <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    <SelectItem value="cash">Efectivo</SelectItem>
                                                    <SelectItem value="card">Tarjeta</SelectItem>
                                                    <SelectItem value="transfer">Transferencia</SelectItem>
                                                </SelectContent>
                                            </Select>
                                        </div>

                                        <div className="flex justify-between items-center py-2">
                                            <span className="font-bold text-lg text-foreground">Total</span>
                                            <span className="text-2xl font-black text-primary">${totalAmount.toFixed(2)}</span>
                                        </div>

                                        {paymentMethod === 'cash' && cart.length > 0 && (
                                            <div className="bg-primary/5 rounded-xl p-4 border border-primary/10 space-y-4 animate-in fade-in slide-in-from-top-2">
                                                <div className="flex flex-col gap-2">
                                                    <Label className="text-[10px] uppercase text-muted-foreground font-bold">Paga con</Label>
                                                    <div className="flex flex-wrap gap-2">
                                                        <Button
                                                            variant="outline"
                                                            size="sm"
                                                            className="h-8 flex-1 text-xs border-primary/20 hover:bg-primary/10"
                                                            onClick={() => setReceivedAmount(totalAmount)}
                                                        >
                                                            Exacto
                                                        </Button>
                                                        {getCashSuggestions(totalAmount).map(suggestion => (
                                                            <Button
                                                                key={suggestion}
                                                                variant="outline"
                                                                size="sm"
                                                                className="h-8 flex-1 text-xs border-primary/20 hover:bg-primary/10"
                                                                onClick={() => setReceivedAmount(suggestion)}
                                                            >
                                                                ${suggestion}
                                                            </Button>
                                                        ))}
                                                    </div>
                                                </div>

                                                <div className="space-y-2">
                                                    <div className="relative">
                                                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                                                        <Input
                                                            type="number"
                                                            placeholder="Cantidad recibida..."
                                                            className="pl-9 h-11 text-lg font-bold"
                                                            value={receivedAmount}
                                                            onChange={(e) => setReceivedAmount(e.target.value)}
                                                        />
                                                    </div>
                                                </div>

                                                {Number(receivedAmount) > totalAmount && (
                                                    <div className="space-y-2">
                                                        <div className="flex justify-between items-center px-1">
                                                            <span className="text-sm font-medium text-muted-foreground italic">Cambio a devolver:</span>
                                                            <span className={`text-xl font-bold ${(Number(receivedAmount) - totalAmount) > currentCashBalance ? 'text-amber-500' : 'text-green-500'}`}>
                                                                ${(Number(receivedAmount) - totalAmount).toFixed(2)}
                                                            </span>
                                                        </div>

                                                        {(Number(receivedAmount) - totalAmount) > currentCashBalance && (
                                                            <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3 flex gap-3 animate-in shake-in">
                                                                <AlertCircle className="h-5 w-5 text-amber-500 shrink-0" />
                                                                <div className="space-y-1">
                                                                    <p className="text-xs font-bold text-amber-600">¡Efectivo Insuficiente!</p>
                                                                    <p className="text-[10px] text-amber-700 leading-tight">
                                                                        No hay suficiente efectivo en caja registradora para este cambio.
                                                                        Si agregaste dinero manualmente, regístralo como un <strong>"Ingreso"</strong> primero.
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        )}

                                        {!activeBox && cart.length > 0 && (
                                            <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4 flex gap-3 animate-pulse">
                                                <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
                                                <div className="space-y-1">
                                                    <p className="text-sm font-bold text-destructive">Caja Cerrada</p>
                                                    <p className="text-xs text-destructive/80 leading-tight">
                                                        Debe abrir la caja en el panel lateral para poder procesar ventas.
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        <Button
                                            className="w-full h-12 text-lg font-bold"
                                            variant="gradient"
                                            disabled={cart.length === 0 || isSubmitting || !activeBox}
                                            onClick={handleCompleteSale}
                                        >
                                            {isSubmitting ? (
                                                <Loader2 className="animate-spin h-5 w-5 mr-2" />
                                            ) : (
                                                <CheckCircle2 className="h-5 w-5 mr-2" />
                                            )}
                                            {!activeBox ? 'Abrir Caja para Vender' : 'Completar Venta'}
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        </div>
                    </div>

                    {/* Cash Sidebar (Right Area) */}
                    <div className="lg:col-span-1 space-y-6">
                        {/* 1. Active Cash Box Status */}
                        {!activeBox ? (
                            <Card className="border-primary/20 bg-primary/5">
                                <CardHeader className="pb-2">
                                    <CardTitle className="text-sm font-bold flex items-center gap-2">
                                        <DollarSign className="h-4 w-4 text-primary" />
                                        Caja Cerrada
                                    </CardTitle>
                                </CardHeader>
                                <CardContent className="space-y-4">
                                    <p className="text-xs text-muted-foreground">Debes abrir la caja para registrar ventas en efectivo.</p>
                                    <div className="space-y-2">
                                        <Label className="text-[10px]">Fondo Inicial</Label>
                                        <Input
                                            type="number"
                                            value={openingBalance}
                                            onChange={(e) => setOpeningBalance(parseFloat(e.target.value))}
                                            className="h-8 text-sm"
                                        />
                                    </div>
                                    <Button className="w-full h-11 sm:h-9 text-sm sm:text-xs" variant="gradient" onClick={handleOpenBox}>
                                        Abrir Caja
                                    </Button>
                                </CardContent>
                            </Card>
                        ) : (
                            <Card className="border-green-500/20 bg-green-500/[0.03] dark:bg-green-500/[0.05] overflow-hidden relative group">
                                <div className="absolute top-0 right-0 p-2">
                                    <div className="flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-green-500/10 border border-green-500/20">
                                        <div className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
                                        <span className="text-[9px] font-black text-green-600 dark:text-green-400 uppercase tracking-tighter">En Vivo</span>
                                    </div>
                                </div>
                                <CardHeader className="pb-2">
                                    <div className="space-y-1">
                                        <CardTitle className="text-[10px] font-black text-muted-foreground uppercase tracking-widest flex items-center gap-2">
                                            Efectivo en Caja
                                        </CardTitle>
                                        <div className="flex items-baseline gap-1">
                                            <span className="text-4xl font-black tracking-tighter text-foreground leading-none">
                                                ${currentCashBalance.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                            </span>
                                        </div>
                                    </div>
                                </CardHeader>
                                <CardContent className="space-y-3 pt-2">
                                    <div className="grid grid-cols-2 gap-2">
                                        <Button
                                            className="h-9 text-[10px] font-bold uppercase tracking-tight bg-white dark:bg-slate-900 hover:bg-muted/50 transition-all border-border shadow-sm"
                                            variant="outline"
                                            onClick={() => setIsMovementOpen(true)}
                                        >
                                            <Plus className="h-3 w-3 mr-1.5 text-primary" />
                                            Ajuste
                                        </Button>
                                        <Button
                                            className="h-9 text-[10px] font-bold uppercase tracking-tight shadow-lg shadow-destructive/20 hover:scale-[1.02] active:scale-[0.98] transition-all"
                                            variant="destructive"
                                            onClick={() => setIsClosingBox(true)}
                                        >
                                            <History className="h-3 w-3 mr-1.5" />
                                            Cerrar
                                        </Button>
                                    </div>
                                    <div className="pt-2 border-t border-green-500/10">
                                        <p className="text-[9px] text-center text-muted-foreground italic truncate">
                                            Caja abierta a las {format(new Date(activeBox.opened_at), "HH:mm")}
                                        </p>
                                    </div>
                                </CardContent>
                            </Card>
                        )}

                        {/* 2. Unified Activity Feed (Reduced view) */}
                        <Card>
                            <CardHeader className="pb-2">
                                <CardTitle className="text-xs font-bold flex items-center gap-2 uppercase tracking-wider text-muted-foreground">
                                    <History className="h-3 w-3" />
                                    Actividad del Turno
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                <div className="max-h-[400px] overflow-y-auto divide-y">
                                    {activeBox ? (
                                        [
                                            ...activeBox.cash_movements
                                                .filter(m => !m.reason.startsWith('Venta de productos'))
                                                .map(m => ({
                                                    time: m.created_at,
                                                    reason: m.reason,
                                                    type: m.type,
                                                    amount: m.amount,
                                                    isSale: false,
                                                    isOpening: false
                                                })),
                                            ...sales.filter(s => (s as any).cash_box_id === activeBox.id).map(s => ({
                                                time: s.created_at,
                                                reason: s.client_name ? `Venta: ${s.client_name}` : 'Venta: Consumidor Final',
                                                type: 'in' as const,
                                                amount: s.total_amount,
                                                isSale: true,
                                                isOpening: false,
                                                items: s.items
                                            })),
                                            {
                                                time: activeBox.opened_at,
                                                reason: 'Apertura de Caja (Capital Inicial)',
                                                type: 'in' as const,
                                                amount: activeBox.opening_balance,
                                                isSale: false,
                                                isOpening: true
                                            }
                                        ].sort((a, b) => new Date(b.time).getTime() - new Date(a.time).getTime()).map((m, idx) => (
                                            (m.isSale && (m as any).items && (m as any).items.length > 0) ? (
                                                <Collapsible key={idx}>
                                                    <CollapsibleTrigger className="w-full p-3 flex justify-between items-center hover:bg-muted/50 transition-colors group data-[state=open]:bg-muted/30">
                                                        <div className="flex flex-col flex-1 min-w-0 mr-2">
                                                            <span className="text-[10px] text-muted-foreground text-left">{format(new Date(m.time), "HH:mm")}</span>
                                                            <span className="text-xs font-medium truncate">{m.reason}</span>
                                                        </div>
                                                        <div className="flex items-center gap-1">
                                                            <div className="flex flex-col items-end">
                                                                <span className={`text-xs font-bold ${m.type === 'in' ? 'text-green-500' : 'text-destructive'}`}>
                                                                    {m.type === 'in' ? '+' : '-'}${m.amount.toFixed(2)}
                                                                </span>
                                                                <Badge className="text-[8px] h-3 px-1" variant="secondary">VENTA</Badge>
                                                            </div>
                                                            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground transition-transform duration-200 group-data-[state=open]:rotate-180 shrink-0" />
                                                        </div>
                                                    </CollapsibleTrigger>
                                                    <CollapsibleContent>
                                                        <div className="px-4 pb-2 space-y-0.5">
                                                            {(m as any).items.map((item: SaleItem) => (
                                                                <div key={item.id} className="flex justify-between items-center text-[10px] text-muted-foreground pl-4 border-l-2 border-primary/20 py-0.5">
                                                                    <span className="truncate flex-1 mr-2">
                                                                        {item.product_name}
                                                                        <span className="text-muted-foreground/50 ml-1">×{item.quantity}</span>
                                                                    </span>
                                                                    <span className="font-mono font-medium">${(item.unit_price * item.quantity).toFixed(2)}</span>
                                                                </div>
                                                            ))}
                                                            <div className="text-[9px] text-muted-foreground/50 pl-4 pt-1">
                                                                {(m as any).items.length} producto{(m as any).items.length !== 1 ? 's' : ''}
                                                            </div>
                                                        </div>
                                                    </CollapsibleContent>
                                                </Collapsible>
                                            ) : (
                                                <div key={idx} className="p-3 flex justify-between items-center group hover:bg-muted/50 transition-colors">
                                                    <div className="flex flex-col flex-1 min-w-0 mr-2">
                                                        <span className="text-[10px] text-muted-foreground">{format(new Date(m.time), "HH:mm")}</span>
                                                        <span className="text-xs font-medium truncate">{m.reason}</span>
                                                    </div>
                                                    <div className="flex flex-col items-end">
                                                        <span className={`text-xs font-bold ${m.type === 'in' ? 'text-green-500' : 'text-destructive'}`}>
                                                            {m.type === 'in' ? '+' : '-'}${m.amount.toFixed(2)}
                                                        </span>
                                                        {m.isOpening && <Badge className="text-[8px] h-3 px-1 bg-green-500/10 text-green-600 border-green-500/20" variant="outline">CAPITAL</Badge>}
                                                    </div>
                                                </div>
                                            )
                                        ))
                                    ) : (
                                        <div className="p-8 text-center text-muted-foreground text-xs italic">
                                            No hay turno activo para mostrar actividad.
                                        </div>
                                    )}
                                    {activeBox && activeBox.cash_movements.length === 0 && sales.filter(s => (s as any).cash_box_id === activeBox.id).length === 0 && (
                                        <div className="p-8 text-center text-muted-foreground text-xs italic">
                                            Sin movimientos en este turno.
                                        </div>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    </div>
                </div>
            ) : (
                <>
                    <div className={`grid ${isMobile ? 'grid-cols-1' : 'lg:grid-cols-4'} gap-6`}>
                        {/* Session List */}
                        <div className={`${!isMobile ? 'lg:col-span-1 lg:border-r lg:pr-6' : ''} space-y-4`}>
                            <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground flex items-center gap-2">
                                <History className="h-4 w-4" />
                                Sesiones Pasadas
                            </h3>
                            <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
                                {boxHistory.map(box => (
                                    <div
                                        key={box.id}
                                        className={`p-4 rounded-xl border transition-all cursor-pointer hover:border-primary/50 group ${selectedHistoryBox?.id === box.id ? 'border-primary bg-primary/5' : 'border-border'}`}
                                        onClick={() => setSelectedHistoryBox(box)}
                                    >
                                        <div className="flex justify-between items-start mb-1">
                                            <span className="text-[10px] font-bold uppercase text-muted-foreground">
                                                {format(new Date(box.opened_at), "d 'de' MMM")}
                                            </span>
                                            <Badge variant="outline" className="text-[9px] h-4">CERRADA</Badge>
                                        </div>
                                        <p className="text-sm font-semibold truncate group-hover:text-primary transition-colors">
                                            {format(new Date(box.opened_at), "HH:mm")} - {box.closed_at ? format(new Date(box.closed_at), "HH:mm") : '??'}
                                        </p>
                                        <div className="flex justify-between items-center mt-2">
                                            <span className="text-xs text-muted-foreground">Final:</span>
                                            <span className="text-sm font-bold text-foreground">${box.actual_balance?.toFixed(2) || '0.00'}</span>
                                        </div>
                                    </div>
                                ))}
                                {boxHistory.length === 0 && !isHistoryLoading && (
                                    <div className="text-center py-8 text-muted-foreground text-xs italic">
                                        No hay sesiones registradas.
                                    </div>
                                )}
                                {isHistoryLoading && (
                                    <div className="flex justify-center py-8">
                                        <Loader2 className="h-6 w-6 animate-spin text-primary" />
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Desktop Detail */}
                        {!isMobile && (
                            <div className="lg:col-span-3">
                                {selectedHistoryBox ? (
                                    <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                                        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                                            <div>
                                                <h2 className="text-2xl font-black flex items-center gap-3">
                                                    Sesión: {format(new Date(selectedHistoryBox.opened_at), "d MMMM, yyyy", { locale: es })}
                                                </h2>
                                                <p className="text-muted-foreground text-sm flex items-center gap-2">
                                                    <UserIcon className="h-4 w-4" />
                                                    Atendido por: {user?.name || 'Admin'}
                                                </p>
                                            </div>
                                            <Badge className="bg-green-500/10 text-green-500 border-green-500/20 px-3 py-1">
                                                Turno Finalizado
                                            </Badge>
                                        </div>
                                        {renderSessionDetailBody(selectedHistoryBox)}
                                    </div>
                                ) : (
                                    <div className="h-[500px] flex flex-col items-center justify-center text-center space-y-4 border-2 border-dashed rounded-3xl opacity-50">
                                        <div className="h-16 w-16 rounded-full bg-muted flex items-center justify-center">
                                            <Search className="h-8 w-8 text-muted-foreground" />
                                        </div>
                                        <div>
                                            <h3 className="text-lg font-bold">Selecciona una sesión</h3>
                                            <p className="text-sm text-muted-foreground max-w-xs mx-auto">
                                                Elige un turno de la lista para ver su actividad detallada y arqueo.
                                            </p>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Mobile Sheet */}
                    {isMobile && (
                        <Sheet open={!!selectedHistoryBox} onOpenChange={(open) => { if (!open) setSelectedHistoryBox(null); }}>
                            <SheetContent side="bottom" className="h-[90vh] overflow-y-auto rounded-t-xl">
                                {selectedHistoryBox && (
                                    <>
                                        <SheetHeader className="mb-4">
                                            <SheetTitle>Sesión: {format(new Date(selectedHistoryBox.opened_at), "d MMMM, yyyy", { locale: es })}</SheetTitle>
                                            <p className="text-sm text-muted-foreground flex items-center gap-2">
                                                <UserIcon className="h-4 w-4" />
                                                Atendido por: {user?.name || 'Admin'}
                                            </p>
                                        </SheetHeader>
                                        <Badge className="bg-green-500/10 text-green-500 border-green-500/20 px-3 py-1 mb-6 inline-block">
                                            Turno Finalizado
                                        </Badge>
                                        {renderSessionDetailBody(selectedHistoryBox)}
                                    </>
                                )}
                            </SheetContent>
                        </Sheet>
                    )}
                </>
            )}

            {/* Dialogs for Petty Cash */}
            <Dialog open={isMovementOpen} onOpenChange={setIsMovementOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Registrar Movimiento de Efectivo</DialogTitle>
                        <DialogDescription>
                            Ingresa los detalles para un ajuste manual de caja (ej: compra de insumos, cambio).
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Tipo de Movimiento</Label>
                            <Select value={movementType} onValueChange={(v: any) => setMovementType(v)}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="in">Ingreso (+)</SelectItem>
                                    <SelectItem value="out">Egreso (-)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Monto ($)</Label>
                            <Input
                                type="number"
                                value={movementAmount}
                                onChange={(e) => setMovementAmount(parseFloat(e.target.value))}
                                placeholder="0.00"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Motivo / Concepto</Label>
                            <Input
                                value={movementReason}
                                onChange={(e) => setMovementReason(e.target.value)}
                                placeholder="Ej: Pago de agua, Compra de café..."
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsMovementOpen(false)}>Cancelar</Button>
                        <Button variant="gradient" onClick={handleAddMovement}>Guardar Movimiento</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={isClosingBox} onOpenChange={setIsClosingBox}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Cierre de Caja y Arqueo</DialogTitle>
                        <DialogDescription>
                            Cuenta el dinero físico presente en caja para finalizar el turno.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="p-4 rounded-xl bg-primary/5 border border-primary/10">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-muted-foreground">Saldo Esperado en Sistema:</span>
                                <span className="font-bold text-lg">${currentCashBalance.toFixed(2)}</span>
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Efectivo Físico Contado ($)</Label>
                            <Input
                                type="number"
                                value={actualBalance}
                                onChange={(e) => setActualBalance(parseFloat(e.target.value))}
                                placeholder="0.00"
                                className="text-xl font-bold"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Diferencia</Label>
                            <div className={`p-2 rounded font-bold text-center ${actualBalance - currentCashBalance === 0 ? 'bg-green-500/10 text-green-500' : 'bg-destructive/10 text-destructive'}`}>
                                {actualBalance - currentCashBalance > 0 ? 'Sobrante: +' : actualBalance - currentCashBalance < 0 ? 'Faltante: ' : 'Cuadre Perfecto: '}
                                ${(actualBalance - currentCashBalance).toFixed(2)}
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label>Notas de Cierre (Opcional)</Label>
                            <Input
                                value={closingNotes}
                                onChange={(e) => setClosingNotes(e.target.value)}
                                placeholder="Observaciones sobre la diferencia o el turno..."
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsClosingBox(false)}>Cancelar</Button>
                        <Button variant="destructive" onClick={handleCloseBox}>Confirmar Cierre de Turno</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </DashboardLayout>
    );
}
